import { useState, useEffect } from 'react';
import { Download, Trash2, Clock, MapPin, FileSignature } from '../../icons/wp-icons';
import { unterschriftApi } from '../../api/unterschriftApi';

// Typ-Badge Farben
const TYP_BADGES = {
  'Auftragsbestätigung': 'bg-green-100 text-green-800',
  'Abnahme': 'bg-blue-100 text-blue-800',
  'Lieferung': 'bg-purple-100 text-purple-800',
  'Kostenvoranschlag akzeptiert': 'bg-yellow-100 text-yellow-800',
  'Sonstiges': 'bg-gray-100 text-gray-800'
};

function UnterschriftenListe({ anfrageId, projektId, kundeName, onUpdate }) {
  const [unterschriften, setUnterschriften] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    loadUnterschriften();
  }, [anfrageId, projektId]);

  const loadUnterschriften = async () => {
    try {
      setLoading(true);
      let data;
      if (projektId) {
        data = await unterschriftApi.getByProjekt(projektId);
      } else if (anfrageId) {
        data = await unterschriftApi.getByAnfrage(anfrageId);
      } else {
        setLoading(false);
        return;
      }
      setUnterschriften(data.unterschriften || []);
    } catch (error) {
      console.error('Fehler beim Laden der Unterschriften:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (unterschrift) => {
    try {
      const blob = await unterschriftApi.download(unterschrift.id);

      // Dateinamen generieren
      const kundeNameClean = (kundeName || 'Kunde').replace(/[^a-zA-Z0-9äöüÄÖÜß\s]/g, '').replace(/\s+/g, '_');
      const typClean = unterschrift.typ.replace(/[^a-zA-Z0-9äöüÄÖÜß\s]/g, '').replace(/\s+/g, '_');
      const datumStr = new Date(unterschrift.datum).toISOString().split('T')[0];
      const filename = `Unterschrift_${kundeNameClean}_${typClean}_${datumStr}.png`;

      // Download auslösen
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Fehler beim Download:', error);
      alert('Fehler beim Download: ' + (error.message || 'Unbekannter Fehler'));
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Möchten Sie diese Unterschrift wirklich löschen?')) {
      return;
    }

    setDeleting(id);
    try {
      await unterschriftApi.delete(id);
      setUnterschriften(unterschriften.filter(u => u.id !== id));
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Fehler beim Löschen:', error);
      alert('Fehler beim Löschen: ' + (error.message || 'Unbekannter Fehler'));
    } finally {
      setDeleting(null);
    }
  };

  const formatDatum = (datum) => {
    return new Date(datum).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (unterschriften.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <FileSignature className="w-12 h-12 mx-auto mb-3 text-gray-300" />
        <p>Noch keine Unterschriften vorhanden.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {unterschriften.map((unterschrift) => (
        <div
          key={unterschrift.id}
          className="border border-gray-200 rounded-xl p-4 hover:border-gray-300 transition"
        >
          <div className="flex items-start gap-4">
            {/* Thumbnail */}
            <div className="flex-shrink-0">
              <img
                src={unterschrift.signatureData}
                alt="Unterschrift"
                className="w-32 h-24 object-contain border border-gray-200 rounded-lg bg-white"
              />
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              {/* Typ Badge nur (Name darunter auf Mobile) */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mb-2">
                <span className={`px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap w-fit ${TYP_BADGES[unterschrift.typ] || TYP_BADGES['Sonstiges']}`}>
                  {unterschrift.typ}
                </span>
                <span className="font-medium text-gray-900 truncate">{unterschrift.signiertVon}</span>
              </div>

              {/* Metadaten */}
              <div className="space-y-1 text-sm text-gray-500">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>{formatDatum(unterschrift.datum)}</span>
                </div>
                {unterschrift.ipAdresse && (
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    <span>IP: {unterschrift.ipAdresse}</span>
                  </div>
                )}
              </div>

              {/* Notizen */}
              {unterschrift.notizen && (
                <p className="mt-2 text-sm text-gray-600 bg-gray-50 rounded-lg p-2">
                  {unterschrift.notizen}
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-2">
              <button
                onClick={() => handleDownload(unterschrift)}
                className="flex items-center gap-1 px-3 py-2 text-sm bg-primary-light text-primary rounded-lg hover:bg-primary hover:text-white transition"
                title="Als PNG herunterladen"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">PNG</span>
              </button>
              <button
                onClick={() => handleDelete(unterschrift.id)}
                disabled={deleting === unterschrift.id}
                className="flex items-center gap-1 px-3 py-2 text-sm bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition disabled:opacity-50"
                title="Löschen"
              >
                {deleting === unterschrift.id ? (
                  <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default UnterschriftenListe;
